import {errorMap} from '@/utils/zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {Box, Button, Container, Stack} from '@mui/material';
import {RhfTextField} from 'mui-rhf-integration';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {useParams} from "react-router-dom";
import {useState} from "react";
import type {HandleSubmitResponse} from "@/components/QrAction";
import Typography from "@mui/material/Typography";

const studentLoginFormSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().optional(),
});

export type StudentLoginFormSchemaType = z.infer<typeof studentLoginFormSchema>;

const InterestedForm = (): React.ReactElement => {
    const {activityId} = useParams();
    const [errorMessage, setErrorMessage] = useState('');
    const [responseMessage, setResponseMessage] = useState('');

    const form = useForm<StudentLoginFormSchemaType>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
        },
        resolver: zodResolver(studentLoginFormSchema, {errorMap}),
    });

    const handleSubmit = async () => {
        const formValues = form.getValues();
        const url = new URL('/interested/student', import.meta.env.VITE_APP_API_ENDPOINT);

        setErrorMessage('');
        setResponseMessage('');

        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                activityId: activityId,
                ...formValues,
            }),
        });

        if (response.ok) {
            const json = (await response.json()) as HandleSubmitResponse;

            if (json.errorCode !== 0) {
                setErrorMessage(json.message);
                return;
            }

            setResponseMessage(json.message);
            return;
        }
    };

    return (
        <Container maxWidth='sm' sx={{mt: 3, backgroundColor: '#fff', borderRadius: '4px', p: 3}}>
            <Typography
                variant='h4'
                sx={{
                    color: '#000',
                    mb: 2,
                }}
            >
                Incoming Student
            </Typography>
            {!!responseMessage && <Box
                sx={{
                    color: '#000',
                    fontSize: '24px',
                }}
            >
                {responseMessage}
            </Box>}
            {!responseMessage && <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
                <Stack>
                    <RhfTextField
                        control={form.control}
                        name='firstName'
                        label='First Name'
                        placeholder='First Name'
                        sx={{my: 1}}
                        onChangeCapture={() => {
                            form.clearErrors();
                        }}
                    />
                    <RhfTextField
                        control={form.control}
                        name='lastName'
                        label='Last Name'
                        placeholder='Last Name'
                        sx={{my: 1}}
                        onChangeCapture={() => {
                            form.clearErrors();
                        }}
                    />
                    <RhfTextField
                        control={form.control}
                        name='email'
                        label='Email'
                        placeholder='Email'
                        sx={{my: 1}}
                        onChangeCapture={() => {
                            form.clearErrors();
                        }}
                    />
                    <Stack
                        direction='row'
                        sx={{
                            justifyContent: (errorMessage ? 'space-between' : 'flex-end'),
                        }}
                    >
                        {errorMessage && <Box
                            sx={{
                                mt: 2,
                                color: 'red',
                                fontSize: '18px',
                            }}
                        >
                            {errorMessage}
                        </Box>}

                        <Button variant='outlined' type='submit' color='primary'>
                            Submit
                        </Button>
                    </Stack>
                </Stack>
            </form>}
        </Container>
    );
};

export default InterestedForm;
